import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../api/auth';
import { b64, encryptWithAesGcm, exportJwk, generateUserKeypair, importPrivateJwk } from '../utils/e2eeCrypto';
import { encryptPrivateJwkForStorage } from '../utils/e2eeCrypto';
import { clearRememberedPrivateKey, setPasskeyWrappedJwk, setRememberedPrivateKey } from '../utils/rememberedKeyStore';
import { canUsePlatformPasskeys, createPlatformPrfCredential, evalPrfSecret } from '../utils/webauthnPrf';
import AiProviderPicker, {
  AiProviderDraft,
  EMPTY_AI_DRAFT,
  isAiDraftConfigured,
} from '../components/AiProviderPicker';
import { llmPreferenceApi, userSecretsApi } from '../api/userSecrets';

const HeartIcon = () => (
  <svg className="auth-logo-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
  </svg>
);

const Register: React.FC = () => {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [fullName, setFullName] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [passphrase2, setPassphrase2] = useState('');
  const [showPassphrase, setShowPassphrase] = useState(false);
  const [passphraseDownloaded, setPassphraseDownloaded] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [protectWithPasskey, setProtectWithPasskey] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiDraft, setAiDraft] = useState<AiProviderDraft>(EMPTY_AI_DRAFT);
  const aiConfigured = isAiDraftConfigured(aiDraft);
  const aiProviderLabels: Record<string, string> = {
    gemini: 'Google Gemini',
    openai: 'OpenAI',
    anthropic: 'Anthropic Claude',
    ollama: 'Ollama',
  };
  const { register, login } = useAuth();
  const navigate = useNavigate();
  const errorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    canUsePlatformPasskeys().then(setPasskeyAvailable).catch(() => setPasskeyAvailable(false));
  }, []);

  useEffect(() => {
    if (!error.trim()) return;
    window.setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }, [error]);

  const generateStrongPassphrase = () => {
    // URL-safe-ish random string; user can still choose their own.
    const bytes = window.crypto.getRandomValues(new Uint8Array(18));
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    const b64 = btoa(bin)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/g, '');
    const out = `edosar-${b64}`;
    setPassphrase(out);
    setPassphrase2(out);
    setPassphraseDownloaded(false);
  };

  const downloadPassphrase = () => {
    const blob = new Blob(
      [
        'eDosarMedical E2EE passphrase\n',
        '\n',
        'Keep this secret. Anyone who has it can decrypt your encrypted documents.\n',
        'If you lose it, you may lose access to E2EE documents.\n',
        '\n',
        `Passphrase: ${passphrase}\n`,
      ],
      { type: 'text/plain;charset=utf-8' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'edosar-passphrase.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setPassphraseDownloaded(true);
  };

  const canSubmit = useMemo(() => {
    const okPass = passphrase.trim().length >= 8 && passphrase === passphrase2 && passphraseDownloaded;
    return okPass && !loading;
  }, [passphrase, passphrase2, passphraseDownloaded, loading]);

  const canGoStep2 = useMemo(() => {
    const okEmail = email.trim().length > 3 && email.includes('@');
    const okPass = password.trim().length >= 6;
    const okMatch = password === password2;
    return okEmail && okPass && okMatch && !loading;
  }, [email, password, password2, loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!passphraseDownloaded) throw new Error('Trebuie să descarci passphrase-ul înainte de înregistrare.');
      if (passphrase.trim().length < 8) throw new Error('Passphrase prea scurt (minim 8 caractere).');
      if (passphrase !== passphrase2) throw new Error('Passphrase-urile nu coincid.');

      const kp = await generateUserKeypair();
      const pub = await exportJwk(kp.publicKey);
      const priv = await exportJwk(kp.privateKey);
      const enc = await encryptPrivateJwkForStorage(priv, passphrase);
      await register(email, password, {
        public_jwk: JSON.stringify(pub),
        encrypted_private_jwk: enc.encrypted_private_jwk,
        kdf_salt_b64: enc.kdf_salt_b64,
        kdf_iterations: enc.kdf_iterations,
        kdf_hash: enc.kdf_hash,
        priv_enc_alg: enc.priv_enc_alg,
        priv_enc_iv_b64: enc.priv_enc_iv_b64,
      }, fullName);

      // Login after registration completes.
      await login(email, password);

      // Best-effort: persist AI provider preference + API key chosen during registration.
      // Backend validates the key with the provider; if it's rejected, the account is
      // still created and the user can retry in Profile -> AI (which surfaces the same
      // error with a clear message).
      try {
        if (aiConfigured) {
          await userSecretsApi.setKey(aiDraft.provider as string, aiDraft.api_key.trim());
          await llmPreferenceApi.set(aiDraft.provider as string, aiDraft.model);
        }
      } catch (e) {
        console.warn('[Register] failed to persist AI prefs after registration', e);
      }

      // Best-effort: remember key on this device, so first decrypt is seamless.
      try {
        if (rememberDevice) {
          if (protectWithPasskey && passkeyAvailable) {
            const { credentialIdB64u } = await createPlatformPrfCredential();
            const prfSalt = window.crypto.getRandomValues(new Uint8Array(32));
            const secret = new Uint8Array(await evalPrfSecret(credentialIdB64u, prfSalt));
            const secretAb = secret.buffer.slice(secret.byteOffset, secret.byteOffset + secret.byteLength);
            const aes = await window.crypto.subtle.importKey('raw', secretAb, { name: 'AES-GCM', length: 256 }, false, [
              'encrypt',
            ]);
            const iv = window.crypto.getRandomValues(new Uint8Array(12));
            const jwkBytes = new TextEncoder().encode(JSON.stringify(priv));
            const ct = await encryptWithAesGcm(aes, jwkBytes, iv);
            await setPasskeyWrappedJwk({
              credential_id_b64u: credentialIdB64u,
              prf_salt_b64: b64.bytesToB64(prfSalt),
              jwk_iv_b64: b64.bytesToB64(iv),
              jwk_ciphertext_b64: b64.bytesToB64(ct),
            });
            await clearRememberedPrivateKey();
          } else {
            const key = await importPrivateJwk(priv, { extractable: false });
            await setRememberedPrivateKey(key);
          }
        }
      } catch {
        // ignore; registration/login succeeded, this is only a UX optimization
      }

      navigate('/dashboard');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Eroare la inregistrare');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-card">
        <div className="auth-logo">
          <HeartIcon />
          <span className="auth-logo-text">eDosarMedical</span>
        </div>

        <h1 className="auth-title">Creeaza un cont</h1>

        <div style={{ marginTop: '-6px', marginBottom: 'var(--sp-4)' }}>
          <div className="muted" style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--sp-2)' }}>
            Pasul {step} din 4
          </div>
          <div aria-label="Progress" style={{ position: 'relative', paddingInline: 6 }}>
            {/* Segmented bar (3 segments between 4 steps) */}
            <div
              style={{
                height: 10,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 8,
                alignItems: 'center',
                marginInline: 6,
              }}
            >
              {[2, 3, 4].map((threshold) => (
                <div
                  key={threshold}
                  style={{
                    height: '100%',
                    borderRadius: 999,
                    background: step >= threshold ? 'var(--color-primary-600)' : 'rgba(148,163,184,0.35)',
                    boxShadow:
                      step >= threshold
                        ? '0 8px 18px rgba(13, 148, 136, 0.25)'
                        : 'inset 0 1px 2px rgba(15,23,42,0.12)',
                    transition: 'background 200ms ease',
                  }}
                />
              ))}
            </div>

            {/* Step dots */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                justifyContent: 'space-between',
                pointerEvents: 'none',
              }}
            >
              {[1, 2, 3, 4].map((n) => {
                const done = step > n;
                const active = step === n;
                const bg = done || active ? 'var(--color-primary-600)' : 'rgba(148,163,184,0.95)';
                const ring = done || active ? 'rgba(13, 148, 136, 0.26)' : 'rgba(148,163,184,0.22)';
                const shadow = active ? '0 12px 26px rgba(13, 148, 136, 0.28)' : '0 6px 14px rgba(15,23,42,0.16)';
                return (
                  <div
                    key={n}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      background: bg,
                      boxShadow: `0 0 0 3px ${ring}, ${shadow}`,
                      display: 'grid',
                      placeItems: 'center',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    {done ? '✓' : n}
                  </div>
                );
              })}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              marginTop: 10,
              fontSize: 'var(--text-xs)',
              paddingInline: 2,
            }}
          >
            <span
              className={step === 1 ? '' : 'muted'}
              style={{ fontWeight: step === 1 ? 700 : 500, textAlign: 'left' }}
            >
              Cont
            </span>
            <span
              className={step === 2 ? '' : 'muted'}
              style={{ fontWeight: step === 2 ? 700 : 500, textAlign: 'center' }}
            >
              Criptare
            </span>
            <span
              className={step === 3 ? '' : 'muted'}
              style={{ fontWeight: step === 3 ? 700 : 500, textAlign: 'center' }}
            >
              AI
            </span>
            <span
              className={step === 4 ? '' : 'muted'}
              style={{ fontWeight: step === 4 ? 700 : 500, textAlign: 'right' }}
            >
              Confirmare
            </span>
          </div>
        </div>

        {error && (
          <div ref={errorRef} className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <>
              <div className="form-group">
                <label className="form-label">Nume complet</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="Ion Popescu"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  autoFocus
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="nume@exemplu.ro"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Parola</label>
                <input
                  className="form-input"
                  type="password"
                  placeholder="Minim 6 caractere"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Confirmă parola</label>
                <input
                  className="form-input"
                  type="password"
                  placeholder="Repetă parola"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>

              <button
                type="button"
                className="btn btn-primary btn-lg btn-full"
                onClick={() => {
                  setError('');
                  if (!canGoStep2) {
                    if (email.trim().length <= 3 || !email.includes('@')) {
                      setError('Introdu un email valid ca să continui.');
                      return;
                    }
                    if (password.trim().length < 6) {
                      setError('Parola trebuie să aibă minim 6 caractere.');
                      return;
                    }
                    if (password !== password2) {
                      setError('Parolele nu coincid.');
                      return;
                    }
                    setError('Completează câmpurile obligatorii ca să continui.');
                    return;
                  }
                  (async () => {
                    const e = email.trim().toLowerCase();
                    try {
                      const res = await authApi.emailAvailable(e);
                      if (!res.available) {
                        setError('Acest email este deja înregistrat. Folosește alt email sau autentifică-te.');
                        return;
                      }
                      setStep(2);
                    } catch (err: any) {
                      // Block when availability check fails: we cannot confirm the email is free, so don't let
                      // the user proceed and waste time on E2EE / AI steps just to hit a 4xx at /register.
                      setError(
                        err?.response?.data?.detail ||
                          err?.message ||
                          'Nu am putut verifica disponibilitatea emailului. Încearcă din nou.'
                      );
                    }
                  })();
                }}
              >
                Continuă
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <div className="form-group">
                <label className="form-label">Passphrase criptare (E2EE)</label>
                <div className="muted" style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--sp-2)' }}>
                  Este cheia ta de acces la documentele criptate. Nu este trimisă către server. Îți cerem să o descarci
                  înainte să finalizezi înregistrarea.
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    className="form-input"
                    type={showPassphrase ? 'text' : 'password'}
                    placeholder="Generează sau introdu o parolă"
                    value={passphrase}
                    onChange={(e) => {
                      setPassphrase(e.target.value);
                      setPassphraseDownloaded(false);
                    }}
                    required
                    disabled={loading}
                    autoFocus
                    style={{ paddingRight: 210 }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowPassphrase((s) => !s)}
                      disabled={loading}
                      title={showPassphrase ? 'Ascunde passphrase' : 'Afișează passphrase'}
                      aria-label={showPassphrase ? 'Ascunde passphrase' : 'Afișează passphrase'}
                      style={{ width: 36, height: 30, padding: 0, display: 'grid', placeItems: 'center' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        {showPassphrase ? (
                          <>
                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                            <circle cx="12" cy="12" r="3" />
                            <path d="M3 3l18 18" />
                          </>
                        ) : (
                          <>
                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                            <circle cx="12" cy="12" r="3" />
                          </>
                        )}
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={generateStrongPassphrase}
                      disabled={loading}
                      style={{ paddingInline: 10 }}
                      title="Generează un passphrase puternic"
                    >
                      Generează
                    </button>
                  </div>
                </div>

              </div>

              <div className="form-group">
                <label className="form-label">Confirmă passphrase</label>
                <input
                  className="form-input"
                  type={showPassphrase ? 'text' : 'password'}
                  placeholder="Repetă passphrase"
                  value={passphrase2}
                  onChange={(e) => {
                    setPassphrase2(e.target.value);
                    setPassphraseDownloaded(false);
                  }}
                  required
                  disabled={loading}
                />
                {passphrase.trim().length >= 8 && passphrase2.trim().length >= 8 && passphrase === passphrase2 && (
                  <div style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-lg btn-full"
                      onClick={() => {
                        setError('');
                        downloadPassphrase();
                      }}
                      disabled={loading}
                      style={{ fontWeight: 800 }}
                      title="Obligatoriu înainte de a continua"
                    >
                      {passphraseDownloaded ? (
                        <>
                          <span aria-hidden="true" style={{ marginRight: 8 }}>
                            ✓
                          </span>
                          Re-descarcă passphrase
                        </>
                      ) : (
                        'Descarcă passphrase (obligatoriu)'
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group" style={{ marginTop: 'var(--sp-2)' }}>
                <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => {
                      setRememberDevice(e.target.checked);
                      if (!e.target.checked) setProtectWithPasskey(false);
                    }}
                    disabled={loading}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ fontSize: 'var(--text-sm)' }}>
                    Ține minte cheia pe acest device (nu vei mai introduce passphrase-ul).
                  </span>
                </label>

                {passkeyAvailable && rememberDevice && (
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 10 }}>
                    <input
                      type="checkbox"
                      checked={protectWithPasskey}
                      onChange={(e) => setProtectWithPasskey(e.target.checked)}
                      disabled={loading}
                      style={{ marginTop: 3 }}
                    />
                    <span style={{ fontSize: 'var(--text-sm)' }}>
                      Protejează cu Passkey (FaceID/TouchID/Windows Hello) pentru a debloca cheia salvată.
                    </span>
                  </label>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-lg"
                  disabled={loading}
                  onClick={() => {
                    setError('');
                    setStep(1);
                  }}
                  style={{ flex: 1 }}
                >
                  Înapoi
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  onClick={() => {
                    setError('');
                    if (passphrase.trim().length < 8) {
                      setError('Passphrase prea scurt (minim 8 caractere).');
                      return;
                    }
                    if (passphrase !== passphrase2) {
                      setError('Passphrase-urile nu coincid.');
                      return;
                    }
                    if (!passphraseDownloaded) {
                      setError('Descarcă mai întâi passphrase-ul (obligatoriu) ca să continui.');
                      return;
                    }
                    setStep(3);
                  }}
                  style={{ flex: 1 }}
                >
                  Continuă
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div style={{ marginBottom: 'var(--sp-3)' }}>
                <div className="muted" style={{ fontSize: 'var(--text-sm)' }}>
                  Furnizorul AI este folosit pentru chat, rezumate și clasificarea automată a documentelor medicale.
                  Alege furnizorul și modelul preferat și adaugă cheia API; cheia este stocată criptat pe server.
                  Pasul este opțional — îl poți sări și configura mai târziu din Profil.
                </div>
              </div>
              <div style={{ marginBottom: 'var(--sp-4)' }}>
                <AiProviderPicker
                  mode="register"
                  value={aiDraft}
                  onChange={setAiDraft}
                />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-lg"
                  disabled={loading}
                  onClick={() => {
                    setError('');
                    setStep(2);
                  }}
                  style={{ flex: 1 }}
                >
                  Înapoi
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-lg"
                  disabled={loading}
                  onClick={() => {
                    setError('');
                    setAiDraft(EMPTY_AI_DRAFT);
                    setStep(4);
                  }}
                  style={{ flex: 1 }}
                >
                  Sari peste
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  disabled={loading}
                  onClick={() => {
                    setError('');
                    const hasAnyInput =
                      !!aiDraft.provider || !!aiDraft.model || aiDraft.api_key.trim().length > 0;
                    if (!hasAnyInput) {
                      // Nothing touched -> treat exactly like "Sari peste"
                      setAiDraft(EMPTY_AI_DRAFT);
                      setStep(4);
                      return;
                    }
                    if (!aiDraft.provider) {
                      setError('Alege un furnizor AI sau apasă „Sari peste".');
                      return;
                    }
                    if (!aiDraft.model) {
                      setError('Alege un model AI sau apasă „Sari peste".');
                      return;
                    }
                    if (aiDraft.api_key.trim().length === 0) {
                      setError(
                        'Introdu cheia API pentru furnizorul ales sau apasă „Sari peste" ca să configurezi mai târziu.'
                      );
                      return;
                    }
                    if (aiDraft.api_key.trim().length < 10) {
                      setError('Cheia API este prea scurtă (minim 10 caractere).');
                      return;
                    }
                    setStep(4);
                  }}
                  style={{ flex: 1 }}
                >
                  Continuă
                </button>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <div
                role="status"
                style={{
                  marginBottom: 'var(--sp-4)',
                  border: '1px solid var(--color-slate-200)',
                  background: 'linear-gradient(145deg, var(--color-primary-50), #fff)',
                  borderRadius: 14,
                  padding: 'var(--sp-4)',
                  boxShadow: '0 10px 26px rgba(15,23,42,0.08)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 'var(--text-lg)' }}>Confirmare</div>
                    <div className="muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                      Verifică datele înainte de a crea contul.
                    </div>
                  </div>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 999,
                      background: 'var(--color-primary-100)',
                      display: 'grid',
                      placeItems: 'center',
                      color: 'var(--color-primary-700)',
                      fontWeight: 900,
                      border: '1px solid var(--color-primary-200)',
                    }}
                    aria-hidden="true"
                  >
                    ✓
                  </div>
                </div>

                <div style={{ height: 1, background: 'rgba(148,163,184,0.35)', margin: 'var(--sp-3) 0' }} />

                <div style={{ display: 'grid', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                    <div className="muted" style={{ fontSize: 'var(--text-sm)' }}>
                      Email
                    </div>
                    <div style={{ fontWeight: 700, wordBreak: 'break-word', textAlign: 'right' }}>{email}</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                    <div className="muted" style={{ fontSize: 'var(--text-sm)' }}>
                      Nume
                    </div>
                    <div style={{ fontWeight: 700, wordBreak: 'break-word', textAlign: 'right' }}>
                      {(fullName || '').trim() || '—'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                    <div className="muted" style={{ fontSize: 'var(--text-sm)' }}>
                      Passphrase descărcat
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <span
                        aria-label={passphraseDownloaded ? 'Descărcat' : 'Nedescărcat'}
                        title={passphraseDownloaded ? 'Descărcat' : 'Nedescărcat'}
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 999,
                          display: 'grid',
                          placeItems: 'center',
                          background: passphraseDownloaded ? 'var(--color-primary-600)' : 'var(--color-danger-500)',
                          color: '#fff',
                          boxShadow: passphraseDownloaded
                            ? '0 8px 18px rgba(13, 148, 136, 0.18)'
                            : 'inset 0 1px 2px rgba(15,23,42,0.10)',
                          border: '1px solid rgba(15,23,42,0.08)',
                          fontSize: 12,
                          fontWeight: 900,
                          lineHeight: 1,
                        }}
                      >
                        {passphraseDownloaded ? '✓' : '×'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                    <div className="muted" style={{ fontSize: 'var(--text-sm)' }}>
                      Ține minte pe acest device
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <span
                        aria-label={rememberDevice ? 'Activ' : 'Inactiv'}
                        title={rememberDevice ? 'Activ' : 'Inactiv'}
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 999,
                          display: 'grid',
                          placeItems: 'center',
                          background: rememberDevice ? 'var(--color-primary-600)' : 'var(--color-danger-500)',
                          color: '#fff',
                          boxShadow: rememberDevice ? '0 8px 18px rgba(13, 148, 136, 0.18)' : 'inset 0 1px 2px rgba(15,23,42,0.10)',
                          border: '1px solid rgba(15,23,42,0.08)',
                          fontSize: 12,
                          fontWeight: 900,
                          lineHeight: 1,
                        }}
                      >
                        {rememberDevice ? '✓' : '×'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                    <div className="muted" style={{ fontSize: 'var(--text-sm)' }}>
                      Protecție Passkey
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
                      {rememberDevice && passkeyAvailable ? (
                        <>
                          <span
                            aria-label={protectWithPasskey ? 'Activ' : 'Dezactivat'}
                            title={protectWithPasskey ? 'Activ' : 'Dezactivat'}
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: 999,
                              display: 'grid',
                              placeItems: 'center',
                              background: protectWithPasskey ? 'var(--color-primary-600)' : 'var(--color-danger-500)',
                              color: '#fff',
                              boxShadow: protectWithPasskey ? '0 8px 18px rgba(13, 148, 136, 0.18)' : 'inset 0 1px 2px rgba(15,23,42,0.10)',
                              border: '1px solid rgba(15,23,42,0.08)',
                              fontSize: 12,
                              fontWeight: 900,
                              lineHeight: 1,
                            }}
                          >
                            {protectWithPasskey ? '✓' : '×'}
                          </span>
                        </>
                      ) : (
                        <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                          indisponibil
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                    <div className="muted" style={{ fontSize: 'var(--text-sm)' }}>
                      Furnizor AI
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                      {aiConfigured ? (
                        <>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>
                              {aiProviderLabels[aiDraft.provider as string] || aiDraft.provider}
                            </div>
                            <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                              {aiDraft.model}
                            </div>
                          </div>
                          <span
                            aria-label="Configurat"
                            title="Configurat"
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: 999,
                              display: 'grid',
                              placeItems: 'center',
                              background: 'var(--color-primary-600)',
                              color: '#fff',
                              boxShadow: '0 8px 18px rgba(13, 148, 136, 0.18)',
                              border: '1px solid rgba(15,23,42,0.08)',
                              fontSize: 12,
                              fontWeight: 900,
                              lineHeight: 1,
                            }}
                          >
                            ✓
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                            neconfigurat (opțional)
                          </span>
                          <span
                            aria-label="Neconfigurat"
                            title="Neconfigurat"
                            style={{
                              width: 18,
                              height: 18,
                              borderRadius: 999,
                              display: 'grid',
                              placeItems: 'center',
                              background: 'rgba(148,163,184,0.95)',
                              color: '#fff',
                              border: '1px solid rgba(15,23,42,0.08)',
                              fontSize: 12,
                              fontWeight: 900,
                              lineHeight: 1,
                            }}
                          >
                            —
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-lg"
                  disabled={loading}
                  onClick={() => {
                    setError('');
                    setStep(3);
                  }}
                  style={{ flex: 1 }}
                >
                  Înapoi
                </button>
                <button type="submit" className="btn btn-primary btn-lg" disabled={!canSubmit} style={{ flex: 1 }}>
                  {loading ? 'Se înregistrează…' : 'Creează cont'}
                </button>
              </div>
            </>
          )}
        </form>

        <div className="auth-footer">
          Ai deja cont? <Link to="/login">Conecteaza-te</Link>
          <div style={{ marginTop: 'var(--sp-3)' }}>
            <Link to="/" style={{ fontSize: 'var(--text-sm)', color: 'var(--color-slate-500)' }}>
              ← Pagina principală
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
